import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import {
  uploadBanner,
  updateBanner,
  deleteBanner,
  getAllDataWithFlag,
} from "../../../../services/CommonService";
import { imgURL, Main_Banner_WIDTH, Main_Banner_HEIGHT, Main_Banner_MAXSIZE_MB } from "../../../Config";

const BannerManager = ({ data }) => {
  const initialFormState = {
    title: "",
    description: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [showForm, setShowForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [items, setItems] = useState([]);
  const [sectionLookup, setSectionLookup] = useState("");

  useEffect(() => {
    if (data) {
      setItems(data.data || []);
    }
  }, [data]);

  useEffect(() => {
    const lookupData = JSON.parse(localStorage.getItem('lookup'));
    if (lookupData) setSectionLookup(lookupData.filter((m) => m.Flag === '6'));
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await getAllDataWithFlag(1);
      const bannerList = response.data.banners;
      setItems(bannerList);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size immediately
      if (file.size > Main_Banner_MAXSIZE_MB * 1024 * 1024) {
        setValidationErrors((prev) => ({ ...prev, image: `File size exceeds ${Main_Banner_MAXSIZE_MB}MB.` }));
        setSelectedImage(null);
        setPreviewImage(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // Store dimensions on the file object for later validation in validateForm
          file.width = img.width;
          file.height = img.height;
          setSelectedImage(file);
          setPreviewImage(URL.createObjectURL(file));
          setValidationErrors((prev) => ({ ...prev, image: "" })); // Clear previous image errors
        };
        img.onerror = () => {
          setValidationErrors((prev) => ({ ...prev, image: "Could not load image. Invalid image file." }));
          setSelectedImage(null);
          setPreviewImage(null);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      // Validate file size immediately
      if (file.size > Main_Banner_MAXSIZE_MB * 1024 * 1024) {
        setValidationErrors((prev) => ({ ...prev, image: `File size exceeds ${Main_Banner_MAXSIZE_MB}MB.` }));
        setSelectedImage(null);
        setPreviewImage(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          file.width = img.width;
          file.height = img.height;
          setSelectedImage(file);
          setPreviewImage(URL.createObjectURL(file));
          setValidationErrors((prev) => ({ ...prev, image: "" })); // Clear previous image errors
        };
        img.onerror = () => {
          setValidationErrors((prev) => ({ ...prev, image: "Could not load image. Invalid image file." }));
          setSelectedImage(null);
          setPreviewImage(null);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setFormData(initialFormState);
    setSelectedImage(null);
    setPreviewImage(null);
    setValidationErrors({});
    setError("");
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.id && !selectedImage)
      errors.image = "Image is required for new banners";
    if (!formData.banner_type)
      errors.banner_type = "Section is required."

    if (selectedImage) {
      if (selectedImage.width < Main_Banner_WIDTH || selectedImage.height < Main_Banner_HEIGHT) {
        errors.image = `Image must be exactly ${Main_Banner_WIDTH} x ${Main_Banner_HEIGHT} pixels. Current: ${selectedImage.width} x ${selectedImage.height}.`;
      }
    } else if (!formData.id && !previewImage) { // For new banners, if no image is selected and no preview exists
      errors.image = "Image is required for new banners";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!validateForm()) return;

    try {
      const formPayload = new FormData();
      formPayload.append("banner_title", formData.title);
      formPayload.append("banner_desc", formData.description);
      formPayload.append("banner_type", formData.banner_type);
      if (selectedImage instanceof File) {
        formPayload.append("image", selectedImage);
      }

      const response = formData.id
        ? await updateBanner(formPayload, formData.id)
        : await uploadBanner(formPayload);

      await fetchData();
      setShowForm(false);
      if (response.status === 200) {
        Swal.fire({
          title: "Success!",
          text: response.data.message,
          icon: "success",
          confirmButtonText: "ok",
        });

        if (formData.id) {
          setItems((prev) =>
            prev.map((item) =>
              item.id === formData.id
                ? {
                  ...item,
                  banner_title: formData.title,
                  banner_desc: formData.description,
                  image_path: response.data.data?.image_path || item.image_path,
                }
                : item
            )
          );
        } else if (response.data.data) {
          setItems((prev) => [...prev, response.data.data]);
        }

        resetForm();
      } else {
        setError(response.data.message || "An error occurred");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setError(error.response?.data?.message || "Failed to connect to the server.");
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleAdd = () => {
    setShowForm(true);
    setFormData({
      id: '',
      title: '',
      description: '',
      banner_type: ''
    });
    setPreviewImage(null);
    setSelectedImage(null);
    setValidationErrors({});
  };

  const handleEdit = (item) => {
    setShowForm(true);
    setFormData({
      id: item.id,
      title: item.banner_title,
      description: item.banner_desc,
      banner_type: item.banner_type
    });
    setPreviewImage(`${imgURL}${item.image_path}`);
    setSelectedImage(null);
    setValidationErrors({});
  };

  const handleDelete = async (item) => {
    Swal.fire({
      title: "Confirm!",
      text: "Are you sure you want to delete this item?",
      icon: "confirm",
      confirmButtonText: "Ok",
      showCancelButton: true,
    }).then(async (r) => {
      if (r.isConfirmed) {
        try {
          const response = await deleteBanner(item.id);
          if (response.status === 200) {
            setItems((prevItems) => prevItems.filter((i) => i.id !== item.id));
            Swal.fire({
              title: "Deleted!",
              text: "Item has been deleted.",
              icon: "success",
              confirmButtonText: "Ok",
            });
          } else {
            console.error("Failed to delete item:", response.data.message || "Unknown error");
          }
        } catch (error) {
          Swal.fire({
            title: "Error!",
            text: error?.response?.data?.message || error?.message,
            icon: "error",
            confirmButtonText: "Ok",
          });
        }
      }
    });
  };

  return (
    <div className="container-fluid mt-4">
      <div className="card">
        <div className="card-header adminCardHeader d-flex justify-content-between align-items-center">
          <h4 className="mb-0">{!showForm ? "Banner Management" : formData.id ? "Update Banner" : "Add New Banner"}</h4>
          <div>
            <button
              className={`btn btn-sm ${showForm ? "btn-warning" : "btn-warning"}`}
              onClick={showForm ? resetForm : handleAdd}
            >
              {showForm ? (<><i className="bi bi-arrow-left me-1"></i> Back</>) : "Add New Banner"}
            </button>
          </div>
        </div>
        <div className="card-body">
          {!showForm ? (
            <div className="table-responsive">
              {items && items.length > 0 ? (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th className="text-center" width="100">S.No.</th>
                      <th>Section</th>
                      <th className="text-center">Image</th>
                      <th className="text-center" >Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>{sectionLookup &&
                          sectionLookup.find(m => m.LookupCode == item.banner_type)?.LookupValue || ""}</td>
                        <td className="text-center">
                          {item.image_path && (
                            <img
                              src={`${imgURL}${item.image_path}`}
                              alt="Banner"
                              style={{
                                width: "50px",
                                height: "30px",
                                objectFit: "cover",
                              }}
                            />
                          )}
                        </td>
                        <td className="text-center">
                          <i
                            className="bi bi-pencil-fill text-primary me-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleEdit(item)}
                          ></i>
                          <i
                            className="bi bi-trash-fill text-danger"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDelete(item)}
                          ></i>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center p-3 text-muted">No data available</div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label required">Section</label>
                  <select
                    className={`form-select ${validationErrors.banner_type ? "is-invalid" : ""}`}
                    name="banner_type"
                    value={formData.banner_type}
                    onChange={handleChange}
                  >
                    <option value="null">Select Section</option>
                    {sectionLookup && sectionLookup.map((m) => (
                      <option value={m.LookupCode} key={m.LookupCode}>{m.LookupValue}</option>
                    ))}
                  </select>
                  {validationErrors.banner_type && (
                    <div className="invalid-feedback">{validationErrors.banner_type}</div>
                  )}
                </div>
                <div className="col-md-12 mb-3">
                  <label className="form-label required">Upload Image ({Main_Banner_WIDTH} × {Main_Banner_HEIGHT} px, Max Size: {Main_Banner_MAXSIZE_MB} MB)</label>
                  <div
                    className={`upload-drop-zone ${isDragging ? "dragover" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current.click()}
                    style={{
                      border: "2px dashed #aaa",
                      padding: "20px",
                      borderRadius: "10px",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: isDragging ? "#f8f9fa" : "white",
                    }}
                  >
                    <p>Drag & drop an image or click to select</p>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      hidden
                      onChange={handleFileChange}
                    />
                    {selectedImage && (
                      <div className="mt-2">
                        <strong>Selected:</strong> {selectedImage.name}
                      </div>
                    )}
                    {previewImage && (
                      <div className="mt-2">
                        <strong>Preview:</strong>
                        <div>
                          <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                              width: "136px",
                              height: "62px",
                              objectFit: "cover",
                              marginTop: "5px",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {validationErrors.image && (
                      <div className="text-danger mt-2">{validationErrors.image}</div>
                    )}
                  </div>
                </div>
                <div className="col-md-12 mt-3 text-end">
                  <button type="submit" className="btn btn-sm btn-warning me-2">
                    {formData.id ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </div>
              {error && (
                <div className="alert alert-danger mt-3" role="alert">
                  {error}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default BannerManager;